// Per-user "completed training" tracking, backed by public.learning_module_completions.
//
// One row per (user, training key); RLS scopes every read and write to the session user, so no
// user variables travel with the operations. Completion is keyed by the logical `module.key` —
// never a version row — and `completed_at` is set-once server-side: the upsert's on_conflict
// does nothing on a re-mark (and returns null, which is why the cache update tolerates a null
// row). See sdk/infra/hasura public_learning_module_completions.
//
// This hook remains the SINGLE SEAM between the UI and storage. Writes are optimistic — the
// Apollo cache updates immediately and rolls back (with a toast) on error — and because every
// hook instance reads the same normalized cache, all surfaces (cards, reader header, progress
// meter, completion footer) stay in sync without a shared external store.

import { useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useDeleteModuleCompletionMutation } from '../queries/deleteModuleCompletion.mutation.generated';
import { GetMyModuleCompletionsDocument, type GetMyModuleCompletionsQuery, useGetMyModuleCompletionsQuery } from '../queries/getMyModuleCompletions.query.generated';
import { useUpsertModuleCompletionMutation } from '../queries/upsertModuleCompletion.mutation.generated';
import type { Learning_Module_Completions_Insert_Input } from '@gql/base-types';
import { useUser } from '@store';

/** moduleKey -> ISO timestamp the user completed it. */
export type CompletionMap = Record<string, string>;

export interface ModuleCompletion {
  /** All completed module keys (for progress summaries). */
  completed: Set<string>;
  isCompleted: (moduleKey: string) => boolean;
  completedAt: (moduleKey: string) => string | undefined;
  /** `moduleId` (the version being viewed) is recorded as analytics metadata when provided. */
  toggle: (moduleKey: string, moduleId?: number) => void;
  /**
   * Resolves `true` once the completion is saved server-side, `false` if the write failed (the
   * optimistic row is already rolled back and the failure toast shown by then). Callers gate any
   * success announcement on the resolved value — never on the optimistic call itself.
   */
  markComplete: (moduleKey: string, moduleId?: number) => Promise<boolean>;
  markIncomplete: (moduleKey: string) => void;
}

type CompletionRows = GetMyModuleCompletionsQuery['learning_module_completions'];

/** Collapse completion rows into a moduleKey -> completed_at map. Exported for unit tests. */
export function rowsToMap(rows: CompletionRows | undefined): CompletionMap {
  const out: CompletionMap = {};
  for (const row of rows ?? []) {
    if (row.module_key && row.completed_at) out[row.module_key] = row.completed_at;
  }
  return out;
}

// Optimistic rows need a locally-unique id for cache normalization. Negative ids can't collide
// with real bigserial ids, and the network row replaces the synthetic one when the result lands.
let syntheticId = 0;
const nextSyntheticId = (): number => --syntheticId;

const SAVE_FAILED = 'Couldn’t save your training progress — please try again.';

export function useModuleCompletion(): ModuleCompletion {
  const { data, refetch } = useGetMyModuleCompletionsQuery({ fetchPolicy: 'cache-and-network' });
  const [upsertCompletion] = useUpsertModuleCompletionMutation();
  const [deleteCompletion] = useDeleteModuleCompletionMutation();

  // The mutation runs under the JWT's default Hasura role (the client sends no explicit role).
  // Every dealer/driver role gets `user_id` from an insert preset, but the `admin` role bypasses
  // presets and must supply it — so internal-staff sessions pass their own session user id. Any
  // non-admin role omits it and lets the preset win (its role schema doesn't expose the column).
  const user = useUser();
  const isAdminRole = user?.claims?.['x-hasura-default-role'] === 'admin';
  const sessionUserId = user?.claims?.['x-hasura-user-id'];

  const rows = data?.learning_module_completions;
  const map = useMemo(() => rowsToMap(rows), [rows]);
  const completed = useMemo(() => new Set(Object.keys(map)), [map]);

  const markComplete = useCallback(
    (moduleKey: string, moduleId?: number): Promise<boolean> => {
      const object: Learning_Module_Completions_Insert_Input = { module_key: moduleKey, module_id: moduleId ?? null };
      // Only the preset-less `admin` role sends user_id; dealer/driver roles must not (the preset
      // owns it, and their role schema rejects the column). Guard on the id so a claim-less admin
      // session still fails loudly rather than silently writing a null user_id. Coerce to a number:
      // the x-hasura-user-id claim is a string at runtime (getClaimsFromToken never numifies it),
      // and user_id is a bigint column.
      if (isAdminRole && sessionUserId != null) object.user_id = Number(sessionUserId);
      return upsertCompletion({
        variables: { object },
        optimisticResponse: {
          insert_learning_module_completions_one: {
            __typename: 'learning_module_completions',
            id: nextSyntheticId(),
            module_key: moduleKey,
            module_id: moduleId ?? null,
            completed_at: new Date().toISOString(),
          },
        },
        update: (cache, result) => {
          // A re-mark's on_conflict does nothing and returns null — the row already exists
          // (in this cache or written from another device), so there is nothing to add.
          const row = result.data?.insert_learning_module_completions_one;
          if (!row) return;
          const existing = cache.readQuery<GetMyModuleCompletionsQuery>({
            query: GetMyModuleCompletionsDocument,
          });
          // Cache not populated yet — the completions query is still in-flight (a mark clicked
          // before it resolved). Seed the list with this row so the UI reflects the save
          // immediately; the authoritative cache-and-network result replaces it on arrival (and
          // includes this row). Without this the successful mark would never reach the cache.
          if (!existing) {
            cache.writeQuery<GetMyModuleCompletionsQuery>({
              query: GetMyModuleCompletionsDocument,
              data: { learning_module_completions: [row] },
            });
            return;
          }
          if (existing.learning_module_completions.some(r => r.module_key === row.module_key)) return;
          cache.writeQuery<GetMyModuleCompletionsQuery>({
            query: GetMyModuleCompletionsDocument,
            data: { ...existing, learning_module_completions: [row, ...existing.learning_module_completions] },
          });
        },
      })
        .then(result => {
          // A conflicting re-mark does nothing server-side and returns a null row (see the
          // mutation's on_conflict). When the row already exists server-side but wasn't in this
          // cache — e.g. completed on another tab/device since the last fetch — the optimistic row
          // we just added has already been rolled back, so the UI would revert to "not complete"
          // on a save that actually succeeded. Refetch to pull the authoritative row back in.
          // Swallow a failed reconcile: the write itself succeeded, so don't surface an error or
          // leak an unhandled rejection — the next cache-and-network read will reconcile.
          if (!result.data?.insert_learning_module_completions_one) refetch().catch(() => {});
          return true;
        })
        .catch(() => {
          toast.error(SAVE_FAILED); // the optimistic row rolls back; UI reverts
          return false;
        });
    },
    [upsertCompletion, isAdminRole, sessionUserId, refetch],
  );

  const markIncomplete = useCallback(
    (moduleKey: string) => {
      void deleteCompletion({
        variables: { moduleKey },
        optimisticResponse: {
          delete_learning_module_completions: {
            __typename: 'learning_module_completions_mutation_response',
            affected_rows: 1,
          },
        },
        update: cache => {
          const existing = cache.readQuery<GetMyModuleCompletionsQuery>({
            query: GetMyModuleCompletionsDocument,
          });
          if (!existing) return;
          cache.writeQuery<GetMyModuleCompletionsQuery>({
            query: GetMyModuleCompletionsDocument,
            data: {
              ...existing,
              learning_module_completions: existing.learning_module_completions.filter(
                r => r.module_key !== moduleKey,
              ),
            },
          });
        },
      }).catch(() => toast.error(SAVE_FAILED));
    },
    [deleteCompletion],
  );

  const toggle = useCallback(
    (moduleKey: string, moduleId?: number) => {
      if (map[moduleKey]) markIncomplete(moduleKey);
      else markComplete(moduleKey, moduleId);
    },
    [map, markComplete, markIncomplete],
  );

  return useMemo<ModuleCompletion>(
    () => ({
      completed,
      isCompleted: (moduleKey: string) => Boolean(map[moduleKey]),
      completedAt: (moduleKey: string) => map[moduleKey],
      toggle,
      markComplete,
      markIncomplete,
    }),
    [map, completed, toggle, markComplete, markIncomplete],
  );
}
